import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, CheckCircle, Clock, XCircle, ChevronRight, UserCheck, Trophy, Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import PostSessionFeedbackModal from "@/components/PostSessionFeedbackModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_LABELS = {
  tennis: "Tennis", pickleball: "Pickleball", golf: "Golf",
  "table-tennis": "Table Tennis", ski: "Skiing", padel: "Padel",
  squash: "Squash", badminton: "Badminton", climbing: "Climbing",
};

const STATUS_CONFIG = {
  pending:   { label: "Pending",   color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/30" },
  confirmed: { label: "Confirmed", color: "text-brand-green",       bg: "bg-brand-green/10 border-brand-green/30" },
  declined:  { label: "Declined",  color: "text-red-400",    bg: "bg-red-400/10 border-red-400/30" },
  completed: { label: "Completed", color: "text-sky-400",    bg: "bg-sky-400/10 border-sky-400/30" },
};


function SessionCard({ session, myId, onAction, onFeedback }) {
  const isInviter = session.inviter_id === myId;
  const partnerName = isInviter ? session.invitee_name : session.inviter_name;
  const partnerId = isInviter ? session.invitee_id : session.inviter_id;
  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.pending;
  const alreadyCheckedIn = (session.checkins || []).includes(myId);
  const alreadyReviewed = Object.keys(session.reviews || {}).includes(myId);
  const [loading, setLoading] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const proposedTimes = session.proposed_times || (session.proposed_time ? [session.proposed_time] : []);
  const hasMultipleTimes = proposedTimes.length > 1;
  const confirmedTime = session.confirmed_time || session.proposed_time;

  const act = async (action, body = {}) => {
    setLoading(action);
    try { await onAction(session.id, action, body); } finally { setLoading(""); }
  };

  const handleConfirm = () => {
    if (hasMultipleTimes && !showTimePicker) {
      setShowTimePicker(true);
      return;
    }
    const time = selectedTime || (proposedTimes[0] || "");
    act("confirm", { selected_time: time });
    setShowTimePicker(false);
  };

  return (
    <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-5 hover:border-brand-green/20 transition-colors dark-section" data-testid="session-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold text-white capitalize">{SPORT_LABELS[session.sport] || session.sport}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
          </div>
          <Link to={`/profile/${partnerId}`} className="text-gray-500 text-sm hover:text-brand-green transition-colors">
            with {partnerName}
          </Link>
        </div>
        <div className="text-xs text-gray-400 flex-shrink-0">
          {new Date(session.created_at).toLocaleDateString()}
        </div>
      </div>

      {/* Proposed / confirmed times */}
      {confirmedTime && session.status !== "pending" && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
          <Clock size={12} />
          <span>{confirmedTime}</span>
        </div>
      )}
      {session.status === "pending" && proposedTimes.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1.5">
            {hasMultipleTimes ? "Proposed times:" : "Proposed time:"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {proposedTimes.map((t) => (
              <span key={t} className="text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-gray-300">
                <Clock size={10} className="inline mr-1 text-gray-500" />{t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Time picker for invitee confirming */}
      {showTimePicker && hasMultipleTimes && (
        <div className="mb-3 p-3 bg-brand-green/5 border border-brand-green/20 rounded-xl" role="group" aria-label="Select a time to confirm">
          <p className="text-xs text-brand-green font-medium mb-2">Pick your preferred time:</p>
          <div className="flex flex-wrap gap-2">
            {proposedTimes.map((t) => (
              <button
                key={t}
                onClick={() => setSelectedTime(t)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  selectedTime === t
                    ? "bg-brand-green text-brand-navy border-brand-green font-bold"
                    : "border-white/20 text-gray-300 hover:border-brand-green/50 hover:text-white"
                }`}
                data-testid={`time-option-${t.replace(/\s+/g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {session.venue_hint && (
        <div className="text-xs text-gray-400 mb-2">Venue: {session.venue_hint}</div>
      )}
      {session.message && (
        <p className="text-xs text-gray-500 bg-gray-100 rounded px-3 py-2 mb-3 italic">"{session.message}"</p>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        {/* Invitee actions on pending */}
        {!isInviter && session.status === "pending" && (
          <>
            <button
              onClick={handleConfirm}
              disabled={!!loading || (showTimePicker && hasMultipleTimes && !selectedTime)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand-orange text-white rounded-lg hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
              data-testid="confirm-session-button"
            >
              <CheckCircle size={13} />
              {loading === "confirm" ? "Confirming..." : (showTimePicker && hasMultipleTimes ? "Confirm Selected" : "Confirm")}
            </button>
            {showTimePicker && (
              <button
                onClick={() => { setShowTimePicker(false); setSelectedTime(null); }}
                className="text-xs px-3 py-1.5 text-gray-400 hover:text-white border border-white/10 rounded-lg transition-colors"
              >
                Cancel
              </button>
            )}
            {!showTimePicker && (
              <button onClick={() => act("decline")} disabled={!!loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-400/40 text-red-400 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-50" data-testid="decline-session-button">
                <XCircle size={13} /> {loading === "decline" ? "Declining..." : "Decline"}
              </button>
            )}
          </>
        )}
        {/* Check-in on confirmed */}
        {session.status === "confirmed" && !alreadyCheckedIn && (
          <button onClick={() => act("checkin")} disabled={!!loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-sky-500/20 border border-sky-500/40 text-sky-400 rounded-lg hover:bg-sky-500/30 transition-colors disabled:opacity-50" data-testid="checkin-button">
            <UserCheck size={13} /> {loading === "checkin" ? "Checking in..." : "Check In"}
          </button>
        )}
        {session.status === "confirmed" && alreadyCheckedIn && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400">
            <CheckCircle size={13} className="text-brand-green" /> Checked in
          </span>
        )}
        {/* Review on completed */}
        {session.status === "completed" && !alreadyReviewed && (
          <button onClick={() => onFeedback(session)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-yellow-400/15 border border-yellow-400/30 text-yellow-400 rounded-lg hover:bg-yellow-400/25 transition-colors" data-testid="leave-review-button">
            <Flag size={13} /> Leave Review
          </button>
        )}
        {session.status === "completed" && alreadyReviewed && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400">
            <Trophy size={13} className="text-yellow-400" /> Review submitted
          </span>
        )}
        {/* Play again CTA for completed sessions */}
        {session.status === "completed" && (
          <a
            href={`/profile/${isInviter ? session.invitee_id : session.inviter_id}?playagain=1`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-brand-orange/30 text-brand-orange rounded-lg hover:bg-brand-orange/10 transition-colors"
            data-testid="play-again-session-btn"
          >
            <Trophy size={12} /> Play Again
          </a>
        )}
      </div>
    </div>
  );
}

export default function SessionsPage() {
  const { user } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const autoShown = useRef(false);

  const enrichSession = useCallback((session) => {
    if (!user) return session;
    const isInviter = session.inviter_id === user.id;
    return { ...session, partner_name: isInviter ? session.invitee_name : session.inviter_name };
  }, [user]);

  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sessions`, { withCredentials: true });
      setSessions(r.data);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-show feedback modal for first unreviewed completed session (once per mount)
  useEffect(() => {
    if (autoShown.current || loading || !sessions.length || !user) return;
    const unreviewed = sessions.find(
      (s) => s.status === "completed" && !Object.keys(s.reviews || {}).includes(user.id)
    );
    if (unreviewed) {
      autoShown.current = true;
      setFeedbackTarget(enrichSession(unreviewed));
    }
  }, [sessions, loading, user, enrichSession]);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    loadSessions();
  }, [user, navigate, loadSessions]);

  const handleAction = async (sessionId, action, body = {}) => {
    const endpoints = {
      confirm: `${API}/sessions/${sessionId}/confirm`,
      decline: `${API}/sessions/${sessionId}/decline`,
      checkin: `${API}/sessions/${sessionId}/checkin`,
    };
    const methods = { confirm: "put", decline: "put", checkin: "put" };
    const r = await axios[methods[action]](endpoints[action], body, { withCredentials: true });
    setSessions((prev) => prev.map((s) => s.id === sessionId ? r.data : s));
    const msgs = { confirm: "Session confirmed!", decline: "Session declined.", checkin: "Checked in! Waiting for your partner." };
    showToast(msgs[action]);
  };

  const tabs = [
    { id: "pending",   label: "Invites",   filter: (s) => s.status === "pending" && s.invitee_id === user?.id },
    { id: "outgoing",  label: "Sent",      filter: (s) => s.status === "pending" && s.inviter_id === user?.id },
    { id: "upcoming",  label: "Upcoming",  filter: (s) => s.status === "confirmed" },
    { id: "completed", label: "Completed", filter: (s) => ["completed", "declined"].includes(s.status) },
  ];

  const currentTab = tabs.find((t) => t.id === tab);
  const filtered = sessions.filter(currentTab?.filter || (() => true));
  const pendingCount = sessions.filter((s) => s.status === "pending" && s.invitee_id === user?.id).length;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-black text-brand-navy">My Sessions</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your play sessions — invite, confirm, check in, and review.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1A1F2E] border border-gray-200 rounded-xl p-1 mb-6 overflow-x-auto dark-section" data-testid="sessions-tabs">
          {tabs.map((t) => {
            const count = t.id === "pending" ? pendingCount : 0;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  tab === t.id ? "bg-brand-orange text-white" : "text-gray-500 hover:text-white"
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 rounded-full font-bold ${tab === t.id ? "bg-navy text-brand-green" : "bg-yellow-400 text-navy"} dark-section`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-sessions">
            <Calendar size={40} className="text-slate-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No sessions here yet.</p>
            {tab === "pending" && (
              <Link to="/find-partners" className="mt-4 inline-flex items-center gap-1.5 text-brand-green text-sm hover:underline">
                Find a partner to play with <ChevronRight size={14} />
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="sessions-list">
            {filtered.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                myId={user.id}
                onAction={handleAction}
                onFeedback={(session) => setFeedbackTarget(enrichSession(session))}
              />
            ))}
          </div>
        )}
      </div>

      {/* Post-session feedback modal */}
      {feedbackTarget && (
        <PostSessionFeedbackModal
          session={feedbackTarget}
          onClose={() => setFeedbackTarget(null)}
          onDone={() => { setFeedbackTarget(null); loadSessions(); showToast("Feedback saved — thank you!"); }}
        />
      )}
    </div>
  );
}
